import axios from 'axios';
import store from './store';

class API {
    /**
     * Appends current locale and dealer to provided url and
     * returns the modified url
     * @param url
     * @returns {string}
     */
    static applyMiddleware(url) {
        const { locale, dealer } = store.getState().app;
        const dealerPart = dealer ? `&dealer=${dealer.slug}` : '';
        return url + `?locale=${locale}${dealerPart}`;
    }

    /**
     * Returns a promise, which on resolve will return an array of all available pens
     * @returns {AxiosPromise}
     */
    static getPens() {
        return axios.get(API.applyMiddleware(`/api/pens`));
    }

    /**
     * Returns a promise, which on resolve will return an array of all available badges
     * @returns {AxiosPromise}
     */
    static getBadges() {
        return axios.get(API.applyMiddleware(`/api/badges`));
    }

    /**
     * Returns a promise, which on resolve will return an object containing pen information alongside
     * further information on parts and other configurable areas
     * @param penSlug
     * @returns {AxiosPromise}
     */
    static getPenBySlug(penSlug) {
        return axios.get(API.applyMiddleware(`/api/pens/${penSlug}`));
    }

    /**
     * Returns a promise, which on resolve will return an object containing pen information
     * @param penSlug
     * @returns {AxiosPromise}
     */
    static getStaticPenBySlug(penSlug) {
        return axios.get(API.applyMiddleware(`/api/pens/static/${penSlug}`));
    }

    /**
     * Not implemented yet
     * @param penSlug
     * @param ConfigHash
     * @returns {AxiosPromise}
     */
    static getPenConfigurationByHash(penSlug, ConfigHash) {
        return axios.get(API.applyMiddleware(`/api/pens/${penSlug}/${ConfigHash}`));
    }

    /**
     * Not implemented yet
     * @param penSlug
     * @param configuration
     * @returns {AxiosPromise}
     */
    static savePenConfiguration(penSlug, configuration) {
        return axios.post(API.applyMiddleware(`/api/pens/${penSlug}`), { configuration });
    }

    /**
     * Takes an order object which will be sent to the server,
     * where further processing and validation will happen
     * @param order
     * @returns {AxiosPromise}
     */
    static sendOrderRequest(order) {
        return axios.post(API.applyMiddleware(`/api/orders`), { data: order });
    }

    /**
     * Returns a promise, which on resolve will return an object containing details about the requested dealer
     * @param dealerSlug
     * @returns {AxiosPromise}
     */
    static getDealerInfo(dealerSlug) {
        return axios.get(`/api/dealers/${dealerSlug}`);
    }

    /**
     * Returns a promise, which on resolve will return an object containing an url to the generated pdf file
     * @param config
     * @returns {AxiosPromise}
     */
    static generatePdf(config) {
        return axios.post(API.applyMiddleware(`/api/pdf`), {
            data: config,
        });
    }

    /**
     * Returns a promise, which on resolve will return an object containing all required texts for the application
     * in the active language
     * @param lang
     * @returns {AxiosPromise}
     */
    static getTexts() {
        return axios.get(API.applyMiddleware(`/api/translations`));
    }

    /**
     * Returns a promise, which on resolve will return an object containing the minimum order amount for the given config
     * @param penSlug
     * @param config
     * @returns {AxiosPromise}
     */
    static getMinimumOrderAmountForConfiguration(penSlug, config) {
        return axios.post(API.applyMiddleware(`/api/pens/${penSlug}/calculate-mov`), {
            data: config,
        });
    }
}

export default API;
